import type { DisplayEvent } from "@/types/api";
import type { EventRow } from "@/utils/schemas";

export type { DisplayEvent, EventRow };

export function toDisplayEvent(
  row: EventRow,
  userId?: null | string,
): DisplayEvent {
  const tiers = (row.event_ticket_kind ?? []).filter(
    (t) => t.status === "active",
  );
  const tier = tiers.length
    ? tiers.reduce((min, t) => (t.price_cents < min.price_cents ? t : min))
    : undefined;
  return {
    address: composeAddress(row),
    currency: tier?.currency ?? null,
    date: row.starts_at ?? null,
    description: row.description ?? "",
    endISO: row.ends_at ?? null,
    flyer_url: row.flyer_url?.replace(/^http:\/\//i, "https://") ?? null,
    geo: parseGeo(row.location_geo),
    id: row.id,
    isAdmin: (row.event_member ?? []).some(
      (m) => m.user_id === userId && m.role === "admin",
    ),
    locationCity: row.location_city ?? null,
    locationNeighborhood: row.location_neighborhood ?? null,
    memberUserId: null,
    placeId: row.location_place_id ?? null,
    priceCents: tier?.price_cents ?? null,
    priceDescription: tier?.description ?? "",
    priceServiceFee: tier?.price_service_fee ?? 0,
    rsvpStatus: null,
    slug: row.slug ?? "",
    startISO: row.starts_at ?? null,
    status: row.status ?? "draft",
    ticketKindId: tier?.id ?? null,
    ticketsRemaining:
      tier?.quantity_total != null && tier?.quantity_sold != null
        ? tier.quantity_total - tier.quantity_sold
        : null,
    title: row.title ?? "",
    venue: row.location_name ?? "",
    visibility: row.visibility ?? "public",
  };
}

/**
 * Encode a lat/lng pair as EWKB hex (SRID 4326, little-endian Point).
 * This is the format Supabase pg_graphql expects for geography columns.
 */
export function toEwkbHex(lng: number, lat: number): string {
  const buf = new ArrayBuffer(25);
  const view = new DataView(buf);
  // byte order: little-endian
  view.setUint8(0, 1);
  // type: Point with SRID flag (0x20000001) in little-endian
  view.setUint32(1, 0x20000001, true);
  // SRID: 4326 in little-endian
  view.setUint32(5, 4326, true);
  // x (longitude), y (latitude) as IEEE 754 doubles
  view.setFloat64(9, lng, true);
  view.setFloat64(17, lat, true);
  const bytes = new Uint8Array(buf);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex.toUpperCase();
}

function composeAddress(row: EventRow): string {
  return [
    row.location_street,
    row.location_city,
    row.location_state,
    row.location_postal_code,
  ]
    .filter(Boolean)
    .join(", ");
}

function parseGeo(point: null | string): { lat: number; lng: number } {
  if (!point) return { lat: 0, lng: 0 };

  // WKT format: POINT(lng lat)
  const wkt = point.match(/POINT\(([^ ]+) ([^ ]+)\)/);
  if (wkt) return { lat: parseFloat(wkt[2]), lng: parseFloat(wkt[1]) };

  // Hex EWKB format returned by Supabase for geography columns
  // Layout: byte-order(2) + type(8) + srid(8) + x-double(16) + y-double(16) = 50 hex chars
  if (/^[0-9a-fA-F]{50}$/.test(point)) {
    const buf = new ArrayBuffer(8);
    const view = new DataView(buf);
    const readDouble = (offset: number) => {
      for (let i = 0; i < 8; i++) {
        view.setUint8(
          i,
          parseInt(point.substring(offset + i * 2, offset + i * 2 + 2), 16),
        );
      }
      return view.getFloat64(0, true);
    };
    const lng = readDouble(18);
    const lat = readDouble(34);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
  }

  return { lat: 0, lng: 0 };
}
